import { createModel } from '@rematch/core';
import { RootModel } from './index';
import API, { ActingAs } from '../API';

/**
 * Where the user is in the onboarding tour, as `/get_user_info` returns it
 * (`UserState.onboarding` on the backend). Null until the tour first starts.
 */
export type OnboardingState = {
  step_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  dismissed_at: string | null;
  feedback: string | null;
};

type userState = {
  is_org_admin: boolean;
  // Dooglie staff (the server says who); gates the staff-only Value page.
  is_dooglie_staff: boolean;
  // When the desktop app last checked in (ISO), or null if it never has. Its
  // presence is what "desktop app installed" means.
  desktop_last_seen_at: string | null;
  // Undefined until `/get_user_info` answers, so nothing tour-related (the
  // nav dot) flashes before we know.
  onboarding: OnboardingState | null | undefined;
  // Whether the user holds one of their org's seats. Starts true so the app
  // renders normally until /get_user_info says otherwise — a brief flash of the
  // real UI beats blocking every user while the first request is in flight.
  has_seat: boolean;
  // Whether this user can prepare a demo (Dooglie staff only). Nothing
  // demo-related renders while false.
  demo_mode_available: boolean;
  // Set while a Dooglie staff member is acting as this user: every request
  // runs as them, and the app shell says so in a red banner.
  acting_as: ActingAs | null;
  // The Dooglie extension answered in this browser before and did not answer
  // this page load: a Chrome update has most likely switched it off.
  extension_switched_off: boolean;
  // Prerelease features this person may not use here (on prod: everyone but
  // Dooglie staff). Undefined until `/get_user_info` answers, so a prerelease
  // entry point stays hidden rather than flashing.
  hidden_features: string[] | undefined;
  // This user's own opt-in to read-only share links for their calls (the Share dialog).
  share_links_enabled: boolean;
};

type UserInfoResponse = {
  is_org_admin?: boolean;
  is_dooglie_staff?: boolean;
  desktop_last_seen_at?: string | null;
  onboarding?: OnboardingState | null;
  has_seat?: boolean;
  demo_mode_available?: boolean;
  acting_as?: ActingAs | null;
  hidden_features?: string[];
  share_links_enabled?: boolean;
};

export const user = createModel<RootModel>()({
  state: {
    is_org_admin: false,
    is_dooglie_staff: false,
    desktop_last_seen_at: null,
    onboarding: undefined,
    has_seat: true,
    demo_mode_available: false,
    acting_as: null,
    extension_switched_off: false,
    hidden_features: undefined,
    share_links_enabled: false,
  } as userState,

  reducers: {
    getUserInfoSuccess(state, data: UserInfoResponse) {
      return {
        ...state,
        is_org_admin: !!data.is_org_admin,
        is_dooglie_staff: !!data.is_dooglie_staff,
        desktop_last_seen_at: data.desktop_last_seen_at ?? null,
        onboarding: data.onboarding ?? null,
        has_seat: data.has_seat !== false,
        demo_mode_available: !!data.demo_mode_available,
        acting_as: data.acting_as ?? null,
        hidden_features: data.hidden_features ?? [],
        share_links_enabled: !!data.share_links_enabled,
      };
    },
    setShareLinksEnabled(state, share_links_enabled: boolean) {
      return { ...state, share_links_enabled };
    },
    setExtensionSwitchedOff(state, extension_switched_off: boolean) {
      return { ...state, extension_switched_off };
    },
    // A 403 seat_required mid-session (an admin revoked the seat while the app
    // was open) lands here, so the block screen appears without a reload.
    setSeatRevoked(state) {
      return { ...state, has_seat: false };
    },
  },

  effects: (dispatch) => ({
    async fetchUserInfo() {
      try {
        const response = await API.post(`/get_user_info`, {});
        dispatch.user.getUserInfoSuccess(response.data);
        // A demo in preparation is only worth asking about for staff.
        if (response.data.demo_mode_available && !response.data.acting_as) {
          void dispatch.demo.fetchStatus();
        }
        return;
      } catch (error) {
        // The flags keep their last values (has_seat starts true), so a
        // failed fetch leaves the app usable rather than blocked.
        console.error('Failed to fetch user info:', error);
      }
    },
  }),
});
